import { CATEGORY_ORDER, getDisplayCategory, isDebtAccount } from './accountTypes';

// How the Accounts list groups its rows: by kind of account (Debt, Savings,
// Investment, Other), by the institution that holds them, or by the year each
// was first used.
export const GROUP_BY_TYPE = 'type';
export const GROUP_BY_INSTITUTION = 'institution';
export const GROUP_BY_FIRST_USE = 'firstUse';
export const GROUP_BY_OPTIONS = [GROUP_BY_TYPE, GROUP_BY_INSTITUTION, GROUP_BY_FIRST_USE];

// What the group of the accounts with no key is called (no institution, no
// movement), by grouping.
export const EMPTY_GROUP_LABEL = {
  [GROUP_BY_INSTITUTION]: 'groupBy.noInstitution',
  [GROUP_BY_FIRST_USE]: 'groupBy.noMovements',
};

const STORAGE_KEY = 'mk.accounts.groupBy';

/** The grouping this device last chose; by type when none or unreadable. */
export function readGroupBy() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return GROUP_BY_OPTIONS.includes(stored) ? stored : GROUP_BY_TYPE;
  } catch {
    return GROUP_BY_TYPE;
  }
}

export function saveGroupBy(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // A private window or blocked storage: the choice lasts for this visit.
  }
}

const byName = (x, y) => (x || '').localeCompare(y || '', undefined, { sensitivity: 'base' });

/**
 * The institution a row is listed under: its own, or its parent's for a
 * sub-account, so a holding stays with its broker and a loan with its person.
 */
export function institutionOf(account, byId) {
  const parent = account.parent_account_id != null ? byId.get(account.parent_account_id) : null;
  return ((parent || account).source || '').trim();
}

/**
 * `{ institution: [accounts] }` in display order: institutions A→Z, the
 * accounts no institution holds (cash, people) last under the key ''. Two
 * spellings of a name that differ only in case share the first one's group.
 * Inside a group, accounts follow the kind order (Debt, Savings, …), then name.
 */
export function groupByInstitution(accounts) {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const groups = new Map();
  accounts.forEach((a) => {
    const name = institutionOf(a, byId);
    const key = name.toLocaleLowerCase();
    if (!groups.has(key)) groups.set(key, { label: name, list: [] });
    groups.get(key).list.push(a);
  });
  const kindRank = (a) => {
    const i = CATEGORY_ORDER.indexOf(getDisplayCategory(a));
    return i < 0 ? CATEGORY_ORDER.length : i;
  };
  const ordered = [...groups.values()].sort((x, y) => (
    !x.label !== !y.label ? (x.label ? -1 : 1) : byName(x.label, y.label)
  ));
  const result = {};
  ordered.forEach(({ label, list }) => {
    list.sort((a, b) => kindRank(a) - kindRank(b) || byName(a.name, b.name));
    result[label] = list;
  });
  return result;
}

/**
 * `{ year: [accounts] }` by the year each account was first used (its earliest
 * movement), oldest year first, the accounts with no movement last under the
 * key ''. A sub-account goes with its parent, and a parent's date is the
 * earliest of its own and its children's, so a person whose movements are all
 * on their loans lands in the year the first loan began. Inside a year,
 * oldest first, then name.
 */
export function groupByFirstUse(accounts) {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const familyOf = (a) => (byId.has(a.parent_account_id) ? a.parent_account_id : a.id);
  const familyFirst = new Map();
  accounts.forEach((a) => {
    const day = a.first_movement_date;
    const known = familyFirst.get(familyOf(a));
    if (day && (!known || day < known)) familyFirst.set(familyOf(a), day);
  });
  // ISO dates, so they compare as text; '' (no movement) sorts last.
  const firstUse = (a) => familyFirst.get(familyOf(a)) || '';
  const sorted = [...accounts].sort((a, b) => {
    const x = firstUse(a);
    const y = firstUse(b);
    if (x === y) return byName(a.name, b.name);
    if (!x || !y) return x ? -1 : 1;
    return x < y ? -1 : 1;
  });
  // Years are integer-like keys, which an object lists ascending ahead of '':
  // the same order as the sort.
  const result = {};
  sorted.forEach((a) => {
    const year = firstUse(a).slice(0, 4);
    (result[year] = result[year] || []).push(a);
  });
  return result;
}

/** The list grouped as chosen; by type it is the overview's own `byType`. */
export function groupAccounts(groupBy, accounts, byType) {
  if (groupBy === GROUP_BY_INSTITUTION) return groupByInstitution(accounts);
  if (groupBy === GROUP_BY_FIRST_USE) return groupByFirstUse(accounts);
  return byType;
}

/** The logo of an institution group: the first of its accounts that has one. */
export function institutionLogo(accounts) {
  const withLogo = (accounts || []).find((a) => a.source_logo_url);
  return withLogo ? withLogo.source_logo_url : null;
}

/**
 * What a row adds to a group that mixes kinds. Debt balances are kept positive
 * for display, so in a group by institution they count against the total: a
 * bank's savings minus its card is what you have there.
 */
export function signedForTotal(account, balance) {
  const value = balance || 0;
  return isDebtAccount(account.account_group, account.account_type) ? -value : value;
}
